import { cardPoints } from '../scoring';
import { ACTION_TYPES } from '../constants';
import { mustTake } from '../actions/turnActions';

// A heuristic opponent for No Thanks!
//
// The game is one binary choice per turn, which makes a genuinely decent bot
// tractable without search. Everything below is arithmetic on public
// information; see "What the bot can see" at the bottom for why that phrase
// is doing real work here.

/**
 * What taking this card actually costs you, in final-score points.
 *
 * Two parts, and the first is the whole game:
 *
 *  - The card's marginal point cost, which is NOT its face value. Adding a
 *    card that bridges or extends a run can cost almost nothing, and can even
 *    be NEGATIVE: holding 15 and 17 scores 32, but 15-16-17 scores 15, so
 *    taking the 16 improves your score by 17 points. Computed by diffing
 *    cardPoints() before and after, which gets every one of these cases right
 *    for free rather than special-casing them.
 *
 *  - Minus the counters piled on the card, since each counter you hold is
 *    worth -1 at scoring.
 *
 * A result <= 0 means the card is free or better.
 */
export const netCostOfTaking = (cards, card, countersOnCard) => (
  cardPoints([...cards, card]) - cardPoints(cards) - countersOnCard
);

/**
 * Bot temperaments. Deliberately deterministic - no RNG - so a seed
 * reproduces a whole game exactly, which is what makes the engine tests and
 * the "replay this deal" affordance work. Variety comes from giving the seats
 * different personalities, not from dice.
 *
 * `patience` is how many points of net cost a bot will keep declining at when
 * it's flush with counters. Higher = milks cards harder before taking them.
 * `reserve` is the counter count at which it stops milking and starts
 * protecting itself from the forced-take rule.
 */
// These numbers are TUNED against simulation, not chosen for flavour, and
// getting there took two corrections worth recording.
//
// First: `patience` was swept (candidate vs. three fixed opponents, 150
// seeded 4-player games per point):
//
//   patience    1     3     5     7     8     9    12    14
//   win rate    7%    9%   27%   48%   58%   46%   48%   43%
//
// Strength climbs steeply to about 8, then plateaus. The first draft used
// 2 / 5 / 9, straddling that cliff, so the seats weren't three temperaments -
// they were one decent bot and two bad ones (6% vs 58% win rate).
//
// Second, and less obvious: moving all three into the plateau did NOT fix it.
// Patience is not a style axis at all - it is strictly a strength axis, and
// in a mixed game the most patient seat wins the war of attrition outright.
// At 7 / 9 / 12 the most patient bot still won 58% against 26% and 19%.
//
// So every bot now plays at the SAME tuned patience, and the variety comes
// from `reserve`, which a second sweep found to be very nearly orthogonal:
//
//   reserve     1     3     5     7     (patience fixed at 9)
//   win rate   19%   28%   25%   33%
//
// A 14-point spread against patience's 51-point one. That's the difference
// between a personality and a handicap: these three play about equally well
// and simply hold their nerve differently - how big a counter cushion they
// insist on keeping before they start taking cards to stay out of the
// forced-take trap.
export const PERSONALITIES = {
  cautious: { patience: 9, reserve: 7, label: 'Cautious' },
  steady: { patience: 9, reserve: 5, label: 'Steady' },
  daring: { patience: 9, reserve: 3, label: 'Daring' },
};

export const PERSONALITY_ORDER = ['steady', 'cautious', 'daring'];

/**
 * The decision.
 *
 * Order matters; each rule below only fires when the ones above it didn't.
 *
 *  1. No counters -> forced to take (p3, Game Play 4). Not a judgement call.
 *
 *  2. Net cost <= 0 -> take. The card is free or actively good. Waiting on a
 *     card this cheap is how you lose it to someone else for nothing.
 *
 *  3. A rival would also find it free -> take it now if it's anywhere near
 *     worth having. This is the rule that stops the bot from politely waiting
 *     on a card that is about to be snapped up. Their cards are face-up on
 *     the table, so this is a fact, not a guess.
 *
 *  4. Running low on counters -> take while the choice is still yours. The
 *     failure mode being avoided is spending your last counters declining
 *     cheap cards, then being forced to take a 34 for free later.
 *
 *  5. Otherwise decline, and let the pile grow.
 *
 * Rule 5 is right far more often than it looks. Every full round puts one
 * counter per player onto the card while costing you exactly one, so if you
 * are going to end up taking it anyway, waiting gains you (players - 1)
 * points a lap. The only real cost of waiting is that somebody else takes it
 * first - which rules 3 and 4 exist to price in.
 */
export const decideAction = (state, personalityKey = 'steady') => {
  if (mustTake(state)) return { type: ACTION_TYPES.TAKE_CARD, reason: 'forced' };

  const me = state.players[state.meta.activePlayerIndex];
  const card = state.currentCard;
  const { patience, reserve } = PERSONALITIES[personalityKey] ?? PERSONALITIES.steady;

  const myCost = netCostOfTaking(me.cards, card, state.countersOnCard);

  if (myCost <= 0) return { type: ACTION_TYPES.TAKE_CARD, reason: 'free' };

  const rivalWouldTakeIt = state.players.some((other) => (
    other.id !== me.id && netCostOfTaking(other.cards, card, state.countersOnCard) <= 0
  ));

  // "Anywhere near worth having" is deliberately tighter than `patience`:
  // this branch is about not losing a bargain, not about bargain-hunting.
  if (rivalWouldTakeIt && myCost <= Math.ceil(patience / 2)) {
    return { type: ACTION_TYPES.TAKE_CARD, reason: 'contested' };
  }

  if (me.counters <= reserve && myCost <= patience * 2) {
    return { type: ACTION_TYPES.TAKE_CARD, reason: 'low-counters' };
  }

  if (myCost <= patience) {
    // Cheap enough to be worth owning, and nobody is fighting for it - but
    // the pile is already big enough that another lap risks more than it
    // gains once the counter spent is priced in.
    if (state.countersOnCard >= card / 2) {
      return { type: ACTION_TYPES.TAKE_CARD, reason: 'ripe' };
    }
  }

  return { type: ACTION_TYPES.DECLINE_CARD, reason: 'waiting' };
};

/**
 * What the bot can see.
 *
 * Every input above is public at a real table: the face-up card, the counters
 * piled on it, and each player's taken cards, which sit face-up in front of
 * them (p2, 1a-i). The bot never reads an opponent's counter supply, which
 * the rulebook tells you to keep hidden (p2, HINT 4) and which this app hides
 * in the UI at Matt's request.
 *
 * That restraint is a real choice, not an accident of the implementation:
 * counter counts are perfectly DERIVABLE by anyone willing to do the
 * bookkeeping, since every decline spends exactly one and every take collects
 * a known pile. A bot that tracked them would not be cheating so much as
 * being inhumanly tidy - but it would still know something the player has
 * been deliberately denied, so it doesn't.
 */
export const personalityFor = (seatIndex) => (
  PERSONALITY_ORDER[seatIndex % PERSONALITY_ORDER.length]
);
